import { useEffect, useRef } from 'react';

import { signInWithCredential, GoogleAuthProvider, getIdToken } from 'firebase/auth';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useDispatch } from 'react-redux';

import { Toast } from '@whelk/components';
import { GOOGLE_CLIENT_ID } from '@whelk/const/src/firebase';
import { FAIL_REASON, LOGIN_RESULT_TYPE } from '@whelk/const/src/login';
import { isApiError } from '@whelk/services/src/common';
import { LoginResBody, postLoginByFirebase } from '@whelk/services/src/user';
import { login, setShowLoginModal } from '@whelk/store/slices/userSlice';
import { trackCustomEvent } from '@whelk/utils';
import { auth } from '@whelk/utils/src/firebase';
import { isOverseas } from '@whelk/const/src/env';

declare let google: any;

export default function useGoogleOneTap() {
  const [user, authLoading] = useAuthState(auth);
  const dispatch = useDispatch();
  const isSigning = useRef(false);

  const onLoginError = (err: any, isOauthFail?: boolean) => {
    isSigning.current = false;
    let fail_reason = FAIL_REASON.OTHER;
    if (isOauthFail) {
      fail_reason = FAIL_REASON.OAUTH_ERROR;
      Toast.error('Login failed');
    } else if (isApiError(err)) {
      fail_reason = FAIL_REASON.SERVER_ERROR;
      Toast.error(err.message);
    }
    trackCustomEvent({
      id: 'login_result',
      result_type: LOGIN_RESULT_TYPE.FAIL, // 失败
      fail_reason,
      page: 'login_popup_page',
      login_type: 'google',
      popup_type: 'onetap',
    });
  };

  const onLoginSuccess = (res: LoginResBody) => {
    isSigning.current = false;
    // Toast.success('Login success');
    dispatch(login(res));
    dispatch(setShowLoginModal(false));
    trackCustomEvent({
      id: 'login_result',
      result_type: LOGIN_RESULT_TYPE.SUCCESS, // 成功
      login_type: 'google',
      page: 'login_popup_page',
      popup_type: 'onetap',
    });
  };

  const handleLogin = async (firebaseIdToken: string) => {
    try {
      if (isSigning.current) return;
      isSigning.current = true;
      const res = await postLoginByFirebase({ authToken: firebaseIdToken });
      onLoginSuccess(res);
    } catch (error) {
      console.error('[one tap login] login error', error);
      onLoginError(error);
    }
  };

  const handleOneTapCredentialResponse = async (response: any) => {
    if (response?.error || !response?.credential) {
      console.error('[one tap login] credential request error', response.error);
      onLoginError(response.error, true);
      return;
    }
    const googleIdToken = response.credential;
    try {
      const authCredential = GoogleAuthProvider.credential(googleIdToken);
      const userCredential = await signInWithCredential(auth, authCredential);
      const firebaseIdToken = await getIdToken(userCredential.user);
      handleLogin(firebaseIdToken);
    } catch (error) {
      console.error('[one tap login] signInWithCredential error', error);
      onLoginError(error);
    }
  };

  const initOneTap = () => {
    if (typeof google === 'undefined' || !google?.accounts?.id) {
      console.error('[one tap login] google.accounts.id is not defined');
      return;
    }

    google?.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: handleOneTapCredentialResponse,
      use_fedcm_for_prompt: false,
    });

    google?.accounts.id.prompt((notification: any) => {
      if (notification.isDisplayed()) {
        // one tap 面板显示
        console.warn('[one tap login] display one tap');
        trackCustomEvent({
          id: 'login_popup_view',
          popup_type: 'onetap',
          page: 'login_popup_page',
        });
      }

      if (notification.isNotDisplayed()) {
        // 不展示 one tap 面板
        const reason = notification.getNotDisplayedReason();
        console.warn('[one tap login] did not show because:', reason);
      }

      if (notification.isSkippedMoment()) {
        // 点击 one tap 面板的关闭按钮，tap_outside 点击外部，user_cancel 用户点击关闭按钮
        const reason = notification.getSkippedReason();
        console.warn('[one tap login] skipped because:', reason);
      }

      if (notification.isDismissedMoment()) {
        const reason = notification.getDismissedReason();
        console.warn('[one tap login] dismissed because:', reason);
      }
    });
  };

  useEffect(() => {
    if (!isOverseas) {
      return;
    }
    if (!authLoading && !user) {
      initOneTap();
    }
  }, [authLoading, user]);

  return {
    authLoading,
  };
}
